export type MarkdownTableAlignment = "left" | "center" | "right" | null;

export interface MarkdownTable {
  header: string[];
  alignments: MarkdownTableAlignment[];
  rows: string[][];
  nextIndex: number;
}

function splitTableRow(line: string): string[] | null {
  const trimmed = line.trim();
  if (!trimmed.includes("|")) return null;

  const content = trimmed.replace(/^\|/, "").replace(/\|$/, "");
  const cells: string[] = [];
  let current = "";
  let escaped = false;
  let inCode = false;

  for (const character of content) {
    if (escaped) {
      current += character;
      escaped = false;
      continue;
    }
    if (character === "\\") {
      escaped = true;
      continue;
    }
    if (character === "`") {
      inCode = !inCode;
      current += character;
      continue;
    }
    if (character === "|" && !inCode) {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += character;
  }
  if (escaped) current += "\\";
  cells.push(current.trim());
  return cells.length >= 2 ? cells : null;
}

function nextNonBlankLine(lines: string[], startIndex: number): number {
  let index = startIndex;
  while (index < lines.length && !lines[index]?.trim()) index += 1;
  return index;
}

export function parseMarkdownTable(lines: string[], startIndex: number): MarkdownTable | null {
  const header = splitTableRow(lines[startIndex] ?? "");
  if (!header) return null;

  const delimiterIndex = nextNonBlankLine(lines, startIndex + 1);
  const delimiters = splitTableRow(lines[delimiterIndex] ?? "");
  if (
    !delimiters
    || delimiters.length !== header.length
    || delimiters.some((cell) => !/^:?-{3,}:?$/.test(cell))
  ) {
    return null;
  }

  const alignments = delimiters.map<MarkdownTableAlignment>((cell) => {
    if (cell.startsWith(":") && cell.endsWith(":")) return "center";
    if (cell.endsWith(":")) return "right";
    if (cell.startsWith(":")) return "left";
    return null;
  });
  const rows: string[][] = [];
  let cursor = delimiterIndex + 1;
  let nextIndex = cursor;

  while (cursor < lines.length) {
    const rowIndex = nextNonBlankLine(lines, cursor);
    const row = splitTableRow(lines[rowIndex] ?? "");
    if (!row || row.length !== header.length) break;
    rows.push(row);
    cursor = rowIndex + 1;
    nextIndex = cursor;
  }

  return { header, alignments, rows, nextIndex };
}
